
import React from 'react';
import { Star, TrendingUp, Sparkles, Hash } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface AdminGamesBulkActionsProps {
  selectedGames: string[];
  bulkAction: string;
  setBulkAction: (action: string) => void;
  bulkCategory: string;
  setBulkCategory: (category: string) => void;
  bulkFakePlayCount: string;
  setBulkFakePlayCount: (count: string) => void;
  bulkFakePlayMin: string;
  setBulkFakePlayMin: (min: string) => void;
  bulkFakePlayMax: string;
  setBulkFakePlayMax: (max: string) => void;
  bulkRatingsCountMin: string;
  setBulkRatingsCountMin: (min: string) => void;
  bulkRatingsCountMax: string;
  setBulkRatingsCountMax: (max: string) => void;
  useFakePlayRange: boolean;
  setUseFakePlayRange: (use: boolean) => void;
  categories: any[];
  onBulkAction: () => void;
  onClearSelection: () => void;
  isLoading: boolean;
}

const AdminGamesBulkActions: React.FC<AdminGamesBulkActionsProps> = ({
  selectedGames,
  bulkAction,
  setBulkAction,
  bulkCategory,
  setBulkCategory,
  bulkFakePlayCount,
  setBulkFakePlayCount,
  bulkFakePlayMin,
  setBulkFakePlayMin,
  bulkFakePlayMax,
  setBulkFakePlayMax,
  bulkRatingsCountMin,
  setBulkRatingsCountMin,
  bulkRatingsCountMax,
  setBulkRatingsCountMax,
  useFakePlayRange,
  setUseFakePlayRange,
  categories,
  onBulkAction,
  onClearSelection,
  isLoading
}) => {
  if (selectedGames.length === 0) return null;

  const isActionDisabled = !bulkAction || 
    (bulkAction === 'change-category' && !bulkCategory) ||
    (bulkAction === 'set-fake-plays' && !useFakePlayRange && (!bulkFakePlayCount || isNaN(parseInt(bulkFakePlayCount)))) ||
    (bulkAction === 'set-fake-plays' && useFakePlayRange && (!bulkFakePlayMin || !bulkFakePlayMax || isNaN(parseInt(bulkFakePlayMin)) || isNaN(parseInt(bulkFakePlayMax)))) ||
    (bulkAction === 'set-random-rating' && (!bulkRatingsCountMin || !bulkRatingsCountMax || isNaN(parseInt(bulkRatingsCountMin)) || isNaN(parseInt(bulkRatingsCountMax))));

  return (
    <Card className="p-4 bg-blue-50 border-blue-200">
      <div className="flex flex-wrap items-center gap-4">
        <span className="text-sm font-medium text-blue-900">
          {selectedGames.length} game(s) selected
        </span>
        
        <Select value={bulkAction} onValueChange={setBulkAction}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Choose Bulk Action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hide">Hide Games</SelectItem>
            <SelectItem value="show">Show Games</SelectItem>
            <SelectItem value="set-featured">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Set Featured
              </div>
            </SelectItem>
            <SelectItem value="unset-featured">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-gray-400" />
                Remove Featured
              </div>
            </SelectItem>
            <SelectItem value="set-trending">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                Set Trending
              </div>
            </SelectItem>
            <SelectItem value="unset-trending">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gray-400" />
                Remove Trending
              </div>
            </SelectItem>
            <SelectItem value="set-new">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-500" />
                Set New
              </div>
            </SelectItem>
            <SelectItem value="unset-new">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gray-400" />
                Remove New
              </div>
            </SelectItem>
            <SelectItem value="change-category">Change Category</SelectItem>
            <SelectItem value="set-fake-plays">
              <div className="flex items-center gap-2">
                <Hash className="w-4 h-4 text-purple-500" />
                Set Fake Play Count
              </div>
            </SelectItem>
            <SelectItem value="set-random-rating">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Set Random Rating
              </div>
            </SelectItem>
            <SelectItem value="delete">Delete Games</SelectItem>
          </SelectContent>
        </Select>

        {bulkAction === 'change-category' && (
          <Select value={bulkCategory} onValueChange={setBulkCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.slug}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {bulkAction === 'set-fake-plays' && (
          <>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="use-range"
                checked={useFakePlayRange}
                onCheckedChange={(checked) => setUseFakePlayRange(checked === true)}
              />
              <label htmlFor="use-range" className="text-sm font-medium">
                Use random range
              </label>
            </div>
            
            {useFakePlayRange ? (
              <>
                <Input
                  type="number"
                  placeholder="Min plays"
                  value={bulkFakePlayMin}
                  onChange={(e) => setBulkFakePlayMin(e.target.value)}
                  className="w-32"
                  min="0"
                />
                <span className="text-sm text-gray-500">to</span>
                <Input
                  type="number"
                  placeholder="Max plays"
                  value={bulkFakePlayMax}
                  onChange={(e) => setBulkFakePlayMax(e.target.value)}
                  className="w-32"
                  min="0"
                />
              </>
            ) : (
              <Input
                type="number"
                placeholder="Enter play count"
                value={bulkFakePlayCount}
                onChange={(e) => setBulkFakePlayCount(e.target.value)}
                className="w-48"
                min="0"
              />
            )}
          </>
        )}

        {bulkAction === 'set-random-rating' && (
          <>
            <Input
              type="number"
              placeholder="Min ratings"
              value={bulkRatingsCountMin}
              onChange={(e) => setBulkRatingsCountMin(e.target.value)}
              className="w-32"
              min="0"
            />
            <span className="text-sm text-gray-500">to</span>
            <Input
              type="number"
              placeholder="Max ratings"
              value={bulkRatingsCountMax}
              onChange={(e) => setBulkRatingsCountMax(e.target.value)}
              className="w-32"
              min="0"
            />
          </>
        )}

        <Button 
          onClick={onBulkAction}
          disabled={isActionDisabled}
          variant={bulkAction === 'delete' ? 'destructive' : 'default'}
          className="min-w-[80px]"
        >
          {isLoading ? 'Applying...' : 'Apply'}
        </Button>

        <Button variant="outline" onClick={onClearSelection}>
          Clear Selection
        </Button>
      </div>
    </Card>
  );
};

export default AdminGamesBulkActions;
